import { Pencil } from 'lucide-react';
import { useState } from 'react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useEnvironment } from '@/react/portainer/environments/queries';
import { useIsPureAdmin } from '@/react/hooks/useUser';
import { usePolicy } from '@/react/portainer/policies/queries/usePolicy';
import { getDockerAppliedPolicies } from '@/react/portainer/policies/utils/policy-types-metadata';
import { PolicyType, TypedPolicy } from '@/react/portainer/policies/types';
import { getStatusDisplayLabel } from '@/react/portainer/policies/DetailView/AttachmentsDataTables/statusUtils';
import {
  AppliedPolicyStatus,
  AppliedPolicyStatusType,
} from '@/react/portainer/environments/types';

import { PageHeader } from '@@/PageHeader';
import { Widget, WidgetBody } from '@@/Widget';
import { Badge, BadgeType } from '@@/Badge';
import { Link } from '@@/Link';
import { LinkButton } from '@@/LinkButton';
import { CollapseExpandButton } from '@@/CollapseExpandButton';
import { TextTip } from '@@/Tip/TextTip';

import { DockerSecurityPolicyReadOnly } from './components/DockerSecurityPolicyReadOnly';
import { DockerSetupPolicyReadOnly } from './components/DockerSetupPolicyReadOnly';
import { DockerRBACPolicyReadOnly } from './components/DockerRBACPolicyReadOnly';
import { DockerRegistryPolicyReadOnly } from './components/DockerRegistryPolicyReadOnly';

export function PoliciesView() {
  const environmentId = useEnvironmentId();
  const environmentQuery = useEnvironment(environmentId);
  const isPureAdmin = useIsPureAdmin();

  const appliedPolicies = getDockerAppliedPolicies(
    environmentQuery.data?.Policies
  );
  const columns =
    appliedPolicies.length === 1
      ? [appliedPolicies]
      : splitAppliedPolicies(appliedPolicies);
  const containerClassName =
    columns.length === 1
      ? 'flex flex-col gap-4'
      : 'flex flex-col lg:flex-row gap-4';
  const columnClassName =
    columns.length === 1 ? 'flex flex-col gap-4' : 'flex flex-1 flex-col gap-4';
  const showPoliciesWidget =
    !environmentQuery.isError &&
    (environmentQuery.isLoading || appliedPolicies.length === 0);

  return (
    <>
      <PageHeader
        title="Policies"
        breadcrumbs={[{ label: 'Fleet Governance Policies' }]}
        reload
      />

      <div className="row">
        <div className="col-sm-12">
          {environmentQuery.isError && (
            <TextTip color="red" className="mb-4">
              Failed to load policies for this environment. Please try again.
            </TextTip>
          )}

          {showPoliciesWidget && (
            <Widget>
              <WidgetBody loading={environmentQuery.isLoading}>
                {!environmentQuery.isLoading &&
                  appliedPolicies.length === 0 && (
                    <div className="text-muted">
                      <p className="mb-2">
                        No policies are currently applied to this environment.
                      </p>
                      <p className="mb-2">
                        Policies allow you to enforce security settings,
                        configure environment options, manage RBAC, and control
                        registry access across multiple environments.
                      </p>
                      {isPureAdmin ? (
                        <p>
                          To create and manage policies, visit the{' '}
                          <Link
                            to="portainer.policies"
                            data-cy="policies-management-link"
                          >
                            Fleet Governance Policies
                          </Link>{' '}
                          page.
                        </p>
                      ) : (
                        <p>
                          Contact your Portainer administrator to enable
                          policies for this environment.
                        </p>
                      )}
                    </div>
                  )}
              </WidgetBody>
            </Widget>
          )}

          {!environmentQuery.isError && appliedPolicies.length > 0 && (
            <div className={containerClassName} data-cy="docker-policies-list">
              {columns.map((column, columnIndex) => (
                <div key={columnIndex} className={columnClassName}>
                  {column.map((item) =>
                    renderPolicySection({
                      item,
                      canEdit: isPureAdmin,
                    })
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function splitAppliedPolicies(
  appliedPolicies: ReturnType<typeof getDockerAppliedPolicies>
) {
  type AppliedPolicyItem = (typeof appliedPolicies)[number];

  return appliedPolicies.reduce<[AppliedPolicyItem[], AppliedPolicyItem[]]>(
    (result, item, index) => {
      result[index % 2].push(item);
      return result;
    },
    [[], []]
  );
}

function renderPolicySection({
  item,
  canEdit,
}: {
  item: ReturnType<typeof getDockerAppliedPolicies>[number];
  canEdit: boolean;
}) {
  const policyId = item.policy?.policyID;
  if (!policyId) {
    return null;
  }

  return (
    <PolicySection
      key={item.key}
      label={item.label}
      policyId={policyId}
      policyName={item.policy?.policyName}
      policyType={item.type}
      status={item.policy?.policyStatus}
      canEdit={canEdit}
    />
  );
}

function getStatusLabel(status: AppliedPolicyStatusType) {
  switch (status) {
    case AppliedPolicyStatusType.Applied:
    case AppliedPolicyStatusType.Failed:
    case AppliedPolicyStatusType.Warning:
    case AppliedPolicyStatusType.InProgress:
    case AppliedPolicyStatusType.NotSupported:
      return getStatusDisplayLabel(status);
    default:
      return status;
  }
}

interface PolicySectionProps {
  label: string;
  policyId: number;
  policyName?: string;
  policyType: PolicyType;
  status?: AppliedPolicyStatus;
  canEdit: boolean;
}

function PolicySection({
  label,
  policyId,
  policyName,
  policyType,
  status,
  canEdit,
}: PolicySectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const policyQuery = usePolicy(policyId);
  const contentId = `policy-section-${policyId}`;

  return (
    <Widget>
      <div className="widget-header w-full">
        <div className="row">
          <div className="col-sm-12">
            <div className="widget-title flex w-full items-center gap-2">
              <CollapseExpandButton
                isExpanded={isExpanded}
                onClick={() => setIsExpanded((value) => !value)}
                data-cy={`collapse-policy-${policyId}`}
              />
              <button
                type="button"
                className="flex-1 flex items-center gap-2 text-left border-0 bg-transparent p-0 focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-5 focus-visible:ring-offset-1"
                onClick={() => setIsExpanded((value) => !value)}
                aria-expanded={isExpanded}
                aria-controls={contentId}
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${label} ${
                  policyName || ''
                } policy section`}
              >
                <span className="flex-1">
                  {label} - {policyName || 'Unknown'}
                </span>
                {status?.Status && (
                  <Badge type={getStatusBadgeType(status.Status)}>
                    {getStatusLabel(status.Status)}
                  </Badge>
                )}
              </button>
              {canEdit && (
                <LinkButton
                  to="portainer.policy"
                  params={{ id: policyId }}
                  icon={Pencil}
                  size="small"
                  color="default"
                  data-cy={`edit-policy-button-${policyId}`}
                >
                  Edit
                </LinkButton>
              )}
            </div>
          </div>
        </div>
      </div>

      <WidgetBody
        loading={policyQuery.isLoading}
        className={isExpanded ? undefined : 'hidden'}
      >
        <div id={contentId} aria-hidden={!isExpanded}>
          {status?.Message && (
            <TextTip color={getStatusTipColor(status.Status)} className="mb-4">
              {status.Message}
            </TextTip>
          )}

          {policyQuery.isError && (
            <TextTip color="red">
              Failed to load this policy. Please try again.
            </TextTip>
          )}

          {policyQuery.data && (
            <PolicyReadOnlyContent
              policy={policyQuery.data}
              policyType={policyType}
            />
          )}
        </div>
      </WidgetBody>
    </Widget>
  );
}

function PolicyReadOnlyContent({
  policy,
  policyType,
}: {
  policy: TypedPolicy<unknown>;
  policyType: PolicyType;
}) {
  switch (policyType) {
    case 'security-docker':
      return <DockerSecurityPolicyReadOnly policy={policy} />;
    case 'setup-docker':
      return <DockerSetupPolicyReadOnly policy={policy} />;
    case 'rbac-docker':
      return <DockerRBACPolicyReadOnly policy={policy} />;
    case 'registry-docker':
      return <DockerRegistryPolicyReadOnly policy={policy} />;
    default:
      return null;
  }
}

const STATUS_BADGE_TYPE: Partial<Record<AppliedPolicyStatusType, BadgeType>> = {
  [AppliedPolicyStatusType.Applied]: 'success',
  [AppliedPolicyStatusType.Failed]: 'danger',
  [AppliedPolicyStatusType.Warning]: 'warn',
  [AppliedPolicyStatusType.InProgress]: 'info',
  [AppliedPolicyStatusType.NotSupported]: 'muted',
};

function getStatusBadgeType(status: AppliedPolicyStatusType): BadgeType {
  return STATUS_BADGE_TYPE[status] ?? 'muted';
}

function getStatusTipColor(
  status?: AppliedPolicyStatusType
): 'red' | 'orange' | 'blue' | undefined {
  if (!status) return undefined;
  if (status === AppliedPolicyStatusType.Failed) return 'red';
  if (status === AppliedPolicyStatusType.Warning) return 'orange';
  if (status === AppliedPolicyStatusType.InProgress) return 'blue';
  return undefined;
}
